import "./cart.css";
import CartItem from "./CartItem";
import {
  getCart,
  WithDraw,
  userVerificationEmail,
  fetchPendingWithdrawals,
} from "../../api/modules/b2droppie.class";
import MultiSelect from "./MultiSelect";
import {
  paymentSuccess,
  setPaymentType,
} from "../../redux/slices/paymentSlice";
import { routes } from "../../routes";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Toaster from "../../components/Toast/Toast";
import { useEffect, useRef, useState } from "react";
import PendingWithdrawals from "./PendingWithdrawals";
import { setCart } from "../../redux/slices/cartSlice";
import { toggleReFetch } from "../../redux/slices/themeSlice";
import Pagination from "../../components/Pagination/Pagination";
import { togglePayment } from "../../redux/slices/accountSlice";
import CommonModal from "../../components/CommonModal/CommonModal";
import ShipSellModal from "../../components/SellItemsConfirm/ShipSellModal";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";

const Cart = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sectionTopRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [multiModalShow, setMultiModalShow] = useState({
    show: false,
    type: "",
  });
  const [cartToast, setCartToast] = useState({
    success: "",
    error: "",
  });
  const user = useSelector((state) => state?.auth?.user);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [selectedItemsMap, setSelectedItemsMap] = useState({});
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const cartItems = useSelector((state) => state.cart.cart.items);
  const reFetchKey = useSelector((state) => state.theme.reFetchKey);
  const [modalShow, setModalShow] = useState({ show: false, type: "" });
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [pendingWithdrawalsData, setPendingWithdrawalsData] = useState([]);
  const [pendingWithdrawalsModal, showPendingWithdrawalsModal] = useState({
    show: false,
    type: "",
  });
  const { currentPage, lastPage } = useSelector((state) => state.cart.cart);

  useEffect(() => {
    if (reFetchKey === "cart" || user) {
      handleCart();
    }
  }, [reFetchKey]);

  useEffect(() => {
    if (reFetchKey === "pendingWithdrawals" || user) {
      getPendingWithdrawals();
    }
  }, [reFetchKey]);

  const handleCart = async (page = 1) => {
    setLoading(true);
    try {
      const response = await getCart({ id: user?.id, page });
      const apiData = response?.data;

      const items = apiData?.data.map((item) => ({
        ...item,
        stock: item.quantity,
        quantity: item.quantity,
      }));

      const itemsCount = items?.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0,
      );

      const totalPrice = items?.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );

      dispatch(
        setCart({
          items,
          currentPage: apiData?.current_page,
          lastPage: apiData?.last_page,
          perPage: apiData?.per_page,
          total: apiData?.total,
          links: apiData?.links,
          itemsCount,
          totalPrice,
        }),
      );

      // setSelectedItems((prevSelected) =>
      //   prevSelected.filter((id) => items.some((item) => item.id === id)),
      // );
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getPendingWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await fetchPendingWithdrawals();
      setPendingWithdrawalsData(response?.data?.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleItemSelection = (item) => {
    setSelectedItemsMap((prev) => {
      const updated = { ...prev };

      if (updated[item.id]) {
        delete updated[item.id];
      } else {
        updated[item.id] = {
          ...item,
          quantity: item.quantity,
        };
      }

      return updated;
    });
  };

  const toggleItemExpansion = (itemId) => {
    setExpandedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const selectedItemsData = Object.values(selectedItemsMap);
  const selectedItemIds = Object.keys(selectedItemsMap).map(Number);

  const withdrawItem = async (singleItem = null) => {
    let product_id = [];
    let quantities = {};
    let payment_amount = 0;

    if (singleItem) {
      product_id = [singleItem.id];
      quantities[singleItem.id] = singleItem.quantity ?? 1;
      payment_amount = Number(singleItem.price) * (singleItem.quantity ?? 1);
    } else {
      const selectedData = selectedItemsData;

      product_id = selectedData.map((item) => item.id);

      selectedData.forEach((item) => {
        quantities[item.id] = item.quantity || 1;
      });

      payment_amount = selectedData.reduce(
        (sum, item) => sum + Number(item.price) * (item.quantity || 1),
        0,
      );
    }
    if (payment_amount < 1.02) {
      setCartToast({
        error: "Minimum withdraw amount is $1.02",
      });
      return;
    }

    if (product_id?.length === 0) return;

    setLoading(true);
    try {
      const response = await WithDraw({
        product_id,
        quantities,
      });

      if (response?.data?.success) {
        dispatch(paymentSuccess(response?.data?.data));
        dispatch(setPaymentType("withdraw"));
        dispatch(togglePayment());
        dispatch(toggleReFetch("cart"));
        setSelectedItemsMap([]);
      }
    } catch (err) {
      if (err?.status === 403) {
        setShowVerifyEmailModal(true);
      }

      setCartToast({
        error: err?.response?.data?.message || "Withdraw failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHeaderAction = async (type) => {
    if (type === "withdraw") {
      withdrawItem();
    } else if (type === "ship" || type === "sell") {
      setMultiModalShow({
        show: true,
        type: type === "sell" ? "sell" : "ship",
      });
    } else {
      setModalShow({ show: true, type });
    }
  };

  const hasDigitalItemSelected = selectedItemsData?.some(
    (item) => item?.digital,
  );

  const userEmailVerification = async () => {
    try {
      const response = await userVerificationEmail({
        email: user?.email,
      });
      cartToast({
        success: response?.data?.message || "",
        error: response?.data?.error?.message || "",
      });
      if (response?.data?.success) {
        setShowVerifyEmailModal(false);
      }
    } catch (err) {
      console.error(err);
      cartToast({
        success: "",
        error: "Something went wrong. Try again.",
      });
    } finally {
      setLoading(false);
      setShowVerifyEmailModal(false);
    }
  };

  return (
    <>
      <Toaster message={cartToast} />
      <div
        ref={sectionTopRef}
        className={`cart-container container ${isDarkMode ? "dark" : "light"}`}
      >
        {/* Header */}
        <div
          className="cart-header"
          style={{
            overflowX: selectedItemsData?.length > 0 ? "auto" : "visible",
          }}
        >
          <div className="cart-header-left">
            <div
              className="header-back-btn"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(routes.HOME);
                }
              }}
            >
              <ChevronLeft style={{ color: "#A6A6A6" }} size={20} />
            </div>
            <div className="cart-header-text">
              <h1 className="cart-title">My Cart</h1>
            </div>
          </div>

          {/* Show action buttons when items are selected */}
          <div style={{ display: "flex", gap: "12px" }}>
            {selectedItemsData?.length > 0 && (
              <>
                <button
                  className={`action-btn action-btn-ship ${
                    hasDigitalItemSelected
                      ? "opacity-50 action-btn-ship-disabled"
                      : ""
                  }`}
                  onClick={() => {
                    if (hasDigitalItemSelected) {
                      setCartToast({
                        error: "One of the selected item is not shipable.",
                      });
                      return;
                    } else handleHeaderAction("ship");
                  }}
                >
                  Ship
                </button>

                <button
                  onClick={() => handleHeaderAction("withdraw")}
                  className="action-btn action-btn-withdraw"
                >
                  Withdraw
                </button>

                <button
                  className="action-btn action-btn-sell"
                  onClick={() => handleHeaderAction("sell")}
                >
                  Sell
                </button>
              </>
            )}
            {/* {pendingWithdrawalsData?.length > 0 && (
              <button
                onClick={() =>
                  showPendingWithdrawalsModal({
                    show: true,
                    type: "pendingWithdrawals",
                  })
                }
                className="deep-primary-monochrome-btn-dark text-fw-500 text-16px text-nowrap w-auto"
              >
                Pending Withdrawals
              </button>
            )} */}
          </div>
        </div>

        {/* Empty Cart Message */}
        {cartItems?.length === 0 && (
          <h4 className="d-center mt-4">Cart is Empty</h4>
        )}

        {cartItems?.length > 0 && (
          <div className="cart-items-container">
            <div className="cart-table-header">
              <div className="cart-col-checkbox-spacer" />
              <span className="cart-col-product">Product</span>
              <div className="cart-col-image-spacer" />
              <span className="cart-col-price">Price</span>
              <span className="cart-col-actions"></span>
              <span className="cart-col-actions-right">Actions</span>
            </div>
            {cartItems?.map((item, index) => (
              <div key={item.id}>
                <CartItem
                  item={item}
                  index={index}
                  onToggleSelect={toggleItemSelection}
                  onToggleExpand={toggleItemExpansion}
                  onWithdraw={() => withdrawItem(item)}
                  isExpanded={expandedItemId === item.id}
                  isSelected={!!selectedItemsMap[item.id]}
                />
              </div>
            ))}
          </div>
        )}

        <Pagination
          lastPage={lastPage}
          currentPage={currentPage}
          onPageChange={handleCart}
          scrollTargetRef={sectionTopRef}
        />
      </div>

      {/* Header Action Modals */}
      {selectedItemsData?.length > 0 && (
        <ShipSellModal
          show={modalShow.show}
          type={modalShow.type}
          setModalShow={setModalShow}
          setNavigationToast={() => {}}
          item={selectedItemsData}
        />
      )}
      <MultiSelect
        data={selectedItemsData}
        show={multiModalShow.show}
        actionType={multiModalShow.type}
        onHide={() => {
          setMultiModalShow({ show: false, type: "" });
          setSelectedItemsMap({});
        }}
        refreshCart={handleCart}
        setCartToast={setCartToast}
        quantities={selectedItemsData.reduce((acc, item) => {
          acc[item.id] = item.quantity || 1;
          return acc;
        }, {})}
      />
      <PendingWithdrawals
        isDarkMode={isDarkMode}
        setLoading={setLoading}
        refreshCart={handleCart}
        setCartToast={setCartToast}
        show={pendingWithdrawalsModal.show}
        pendingWithdrawalsData={pendingWithdrawalsData}
        refreshPendingWithdrawals={getPendingWithdrawals}
        onHide={() => showPendingWithdrawalsModal({ show: false, type: "" })}
      />
      <CommonModal
        show={showVerifyEmailModal}
        onClose={() => setShowVerifyEmailModal(false)}
        onConfirm={() => userEmailVerification()}
        title={
          <h4
            className={`${isDarkMode ? "text-light-lavender-purple" : "text-deep-purple"} w-100 d-center`}
          >
            Email not Verified!
          </h4>
        }
        body={
          <>
            <p
              className={`${isDarkMode ? "text-muter-lavender-purple" : "text-charcoal-gray"} mb-2`}
            >
              Please verify your email address. Click Here to generate an email.
            </p>
          </>
        }
        showFooter={true}
        modalWidth="w-lg-35"
        isDarkMode={isDarkMode}
        confirmText="Send Email!"
        btnStyle="filled-danger-btn-dark"
      />
      <BackdropLoader show={loading} />
    </>
  );
};

export default Cart;
